import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription, firstValueFrom } from 'rxjs';

import {
  WebsiteCrawlJob,
  WebsiteCrawlHistoryItem,
  WebsiteCrawlPageResult,
  WebsiteCrawlRequest,
  WebsiteCrawlScopeMode,
  WebsiteCrawlStatus,
  WebsiteCrawlStatusResponse,
  WebsiteCrawlQaListResponse,
  isTerminalStatus
} from './website-crawl.models';
import { WebsiteCrawlService } from './website-crawl.service';

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

type TrainingFilter = 'COMPLETED' | 'ALL' | 'ACTIVE' | 'FAILED';
type CounterRow = { key: string; label: string; value: number };

/**
 * Website Knowledge Crawler.
 *
 * Starts a crawl through the GRMS Spring API and polls it until it finishes.
 * Jobs live in component memory only — there is no server-side crawl history
 * endpoint, so leaving or refreshing the page loses the job list. The crawls
 * themselves keep running in the crawler regardless.
 */
@Component({
  selector: 'app-website-url-ingestion',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './website-url-ingestion.component.html',
  styleUrl: './website-url-ingestion.component.css'
})
export class WebsiteUrlIngestionComponent implements OnInit, OnDestroy {

  baseUrl = '';
  maxPages = 10;
  maxDepth = 2;
  concurrency = 2;
  scopeMode: WebsiteCrawlScopeMode = 'EXACT_ORIGIN';
  respectRobotsTxt = true;
  saveRenderedHtml = false;
  saveScreenshot = false;
  showAdvanced = false;
  navigationTimeoutMs = 60000;
  waitAfterLoadMs = 1000;
  interactWithCarousels = true;
  maxSlidesPerCarousel = 20;
  slideTransitionWaitMs = 500;

  /** True only while the create request is in flight, to block double submits. */
  starting = false;
  loadingHistory = false;
  actionCrawlId: string | null = null;
  jobs: WebsiteCrawlJob[] = [];
  selectedCrawlId: string | null = null;
  selectedPage: WebsiteCrawlPageResult | null = null;
  trainingFilter: TrainingFilter = 'COMPLETED';
  training = false;
  trainingProgress = '';
  downloadingCrawlId: string | null = null;
  qaGeneratingCrawlId: string | null = null;
  qaDownloadingCrawlId: string | null = null;
  qaLists = new Map<string, WebsiteCrawlQaListResponse>();
  toasts: Toast[] = [];

  private pollers = new Map<string, Subscription>();
  private artifactLoads = new Set<string>();
  private toastId = 0;

  constructor(private crawlService: WebsiteCrawlService) {}

  ngOnInit(): void {
    this.loadHistory();
  }

  ngOnDestroy(): void {
    this.pollers.forEach(subscription => subscription.unsubscribe());
    this.pollers.clear();
  }

  get selectedJob(): WebsiteCrawlJob | null {
    return this.jobs.find(job => job.crawlId === this.selectedCrawlId) ?? null;
  }

  get pages(): WebsiteCrawlPageResult[] {
    return this.selectedJob?.detail?.documents ?? [];
  }

  get trainingRows(): WebsiteCrawlJob[] {
    const rows = this.jobs.filter(job => {
      if (this.isActive(job.status)) {
        return this.trainingFilter === 'ALL' || this.trainingFilter === 'ACTIVE';
      }
      if (job.status === 'COMPLETED') {
        return this.trainingFilter === 'ALL' || this.trainingFilter === 'COMPLETED';
      }
      return this.trainingFilter === 'ALL' || this.trainingFilter === 'FAILED';
    });
    return rows;
  }

  startCrawl(): void {
    if (this.starting) {
      return;
    }

    const normalized = this.normalizeBaseUrl(this.baseUrl);
    if (!normalized) {
      this.showToast('Please enter a valid http or https website address.', 'error');
      return;
    }

    this.starting = true;
    this.crawlService.startCrawl(this.buildRequest(normalized)).subscribe({
      next: response => {
        this.starting = false;
        this.removeLocalJobsForBaseUrl(response.baseUrl, response.crawlId);
        this.jobs = [
          {
            crawlId: response.crawlId,
            baseUrl: response.baseUrl,
            status: response.status,
            createdAt: response.createdAt,
            detail: null,
            error: null
          },
          ...this.jobs
        ];
        this.selectedCrawlId = response.crawlId;
        this.selectedPage = null;
        this.startPolling(response.crawlId);
        this.showToast('Crawl started successfully.', 'success');
      },
      error: (message: string) => {
        this.starting = false;
        this.showToast(message, 'error');
      }
    });
  }

  selectJob(job: WebsiteCrawlJob): void {
    this.selectedCrawlId = job.crawlId;
    this.selectedPage = null;
    this.loadArtifacts(job);
  }

  recrawl(job: WebsiteCrawlJob): void {
    if (this.actionCrawlId || this.isActive(job.status)) {
      return;
    }
    this.actionCrawlId = job.crawlId;
    this.crawlService.recrawl(job.crawlId).subscribe({
      next: response => {
        this.actionCrawlId = null;
        this.removeLocalJobsForBaseUrl(response.baseUrl, response.crawlId);
        const created: WebsiteCrawlJob = {
          crawlId: response.crawlId, baseUrl: response.baseUrl, status: response.status,
          createdAt: response.createdAt, detail: null, error: null
        };
        this.jobs = [created, ...this.jobs];
        this.selectedCrawlId = created.crawlId;
        this.selectedPage = null;
        this.startPolling(created.crawlId);
        this.showToast('Recrawl started successfully.', 'success');
      },
      error: (message: string) => {
        this.actionCrawlId = null;
        this.showToast(message, 'error');
      }
    });
  }

  deleteCrawl(job: WebsiteCrawlJob): void {
    if (this.actionCrawlId || this.isActive(job.status)
        || !window.confirm(`Delete the saved crawl for ${job.baseUrl}? This removes its extracted content.`)) {
      return;
    }
    this.actionCrawlId = job.crawlId;
    this.crawlService.deleteCrawl(job.crawlId).subscribe({
      next: () => {
        this.actionCrawlId = null;
        this.stopPolling(job.crawlId);
        this.jobs = this.jobs.filter(item => item.crawlId !== job.crawlId);
        if (this.selectedCrawlId === job.crawlId) {
          this.selectedCrawlId = this.jobs[0]?.crawlId ?? null;
          this.selectedPage = null;
          if (this.jobs[0]) this.loadArtifacts(this.jobs[0]);
        }
        this.showToast('Crawl and extracted content deleted.', 'success');
      },
      error: (message: string) => {
        this.actionCrawlId = null;
        this.showToast(message, 'error');
      }
    });
  }

  openPage(page: WebsiteCrawlPageResult): void {
    this.selectedPage = page;
  }

  clearPage(): void {
    this.selectedPage = null;
  }

  isActive(status: WebsiteCrawlStatus): boolean {
    return !isTerminalStatus(status);
  }

  statusClass(status: WebsiteCrawlStatus): string {
    switch (status) {
      case 'COMPLETED':
        return 'status-done';
      case 'FAILED':
        return 'status-failed';
      case 'INTERRUPTED':
        return 'status-interrupted';
      case 'RUNNING':
        return 'status-processing';
      default:
        return 'status-pending';
    }
  }

  formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  }

  formatCount(value?: number | null): string {
    return value === null || value === undefined ? '-' : String(value);
  }

  parsedDocumentCount(job: WebsiteCrawlJob): string {
    return this.formatCount(this.firstNumber(
      job.detail?.parsedDocumentCount,
      this.reportNumber(job, 'total_documents_parsed'),
      this.reportNumber(job, 'total_parsed_documents'),
      job.detail?.documentCount
    ));
  }

  ocrReadCount(job: WebsiteCrawlJob): string {
    return this.formatCount(this.firstNumber(
      job.detail?.ocrReadCount,
      job.detail?.ocrDocumentCount,
      this.reportNumber(job, 'total_documents_with_ocr_text'),
      this.reportNumber(job, 'total_ocr_documents_read'),
      this.reportNumber(job, 'ocr_documents_read')
    ));
  }

  skippedRows(job: WebsiteCrawlJob): CounterRow[] {
    return this.counterRows(this.firstRecord(
      job.detail?.skippedBreakdown,
      job.detail?.skipBreakdown,
      job.detail?.skipReasons,
      job.detail?.report?.skipped_breakdown,
      job.detail?.report?.skip_breakdown,
      job.detail?.report?.skip_reasons,
      job.detail?.report?.skip_reason_counts
    ), [
      'external_domain',
      'duplicate_url',
      'max_pages',
      'excluded_extension',
      'unsupported_protocol',
      'robots_disallowed',
      'query_variant',
      'max_depth'
    ]);
  }

  duplicateRows(job: WebsiteCrawlJob): CounterRow[] {
    return this.counterRows(this.firstRecord(
      job.detail?.duplicateBreakdown,
      job.detail?.duplicatesBreakdown,
      job.detail?.report?.duplicate_breakdown,
      job.detail?.report?.duplicates_breakdown,
      job.detail?.report?.duplicate_reason_counts
    ), [
      'already_queued',
      'already_processed',
      'canonical_duplicate',
      'query_normalization_duplicate',
      'same_content_hash',
      'desktop_mobile_duplicate'
    ]);
  }

  ocrRows(job: WebsiteCrawlJob): CounterRow[] {
    const report = job.detail?.report;
    const ocr = report?.ocr;
    if (!ocr && job.detail?.ocrReadCount == null) {
      return [];
    }
    return [
      { key: 'images_discovered', label: 'Images Discovered', value: Number(ocr?.images_discovered ?? 0) },
      { key: 'images_eligible', label: 'OCR Eligible', value: Number(ocr?.images_eligible ?? 0) },
      { key: 'images_processed', label: 'OCR Attempted', value: Number(ocr?.images_processed ?? 0) },
      { key: 'successful', label: 'OCR Successful', value: Number(ocr?.successful ?? job.detail?.ocrReadCount ?? 0) },
      { key: 'low_confidence', label: 'OCR Low Confidence', value: Number(ocr?.low_confidence ?? 0) },
      { key: 'failed', label: 'OCR Failed', value: Number(ocr?.failed ?? 0) },
      { key: 'skipped_decorative', label: 'OCR Skipped Decorative', value: Number(report?.total_decorative_images ?? 0) },
      { key: 'words_added', label: 'OCR Words Added', value: Number(ocr?.words_included ?? ocr?.words_extracted ?? 0) }
    ];
  }

  hasCounterRows(rows: CounterRow[]): boolean {
    return rows.length > 0;
  }

  faqCount(page: WebsiteCrawlPageResult): number {
    return page.faqs?.length ?? 0;
  }

  hasTrainableDocuments(job: WebsiteCrawlJob): boolean {
    return job.status === 'COMPLETED';
  }

  availableTrainingRows(): WebsiteCrawlJob[] {
    return this.jobs.filter(job => this.hasTrainableDocuments(job));
  }

  canTrainCompletedCrawls(): boolean {
    return !this.training && this.availableTrainingRows().length > 0;
  }

  trainingRecordCount(job: WebsiteCrawlJob): string {
    return this.formatCount(job.detail?.artifacts?.documentsJsonlRecordCount ?? job.detail?.documentCount);
  }

  trainingStatusText(job: WebsiteCrawlJob): string {
    if (this.isActive(job.status)) {
      return 'Crawl is still running.';
    }
    if (job.status === 'COMPLETED') {
      return 'Ready for training';
    }
    return 'Not trainable';
  }

  canDownloadDocuments(job: WebsiteCrawlJob): boolean {
    return job.status === 'COMPLETED' && !this.downloadingCrawlId;
  }

  async downloadDocuments(job: WebsiteCrawlJob): Promise<void> {
    if (!this.canDownloadDocuments(job)) {
      this.showToast('Extracted documents are available only after the crawl is completed.', 'error');
      return;
    }

    this.downloadingCrawlId = job.crawlId;
    try {
      const pageSize = 500;
      let offset = 0;
      let total = Number.MAX_SAFE_INTEGER;
      const documents: WebsiteCrawlPageResult[] = [];

      while (offset < total) {
        const response = await firstValueFrom(this.crawlService.getCrawlDocuments(job.crawlId, pageSize, offset));
        total = response.total ?? 0;
        documents.push(...(response.items ?? []));
        if (!response.items?.length) {
          break;
        }
        offset += response.items.length;
      }

      if (!documents.length) {
        this.showToast('No extracted documents were found for this crawl.', 'error');
        return;
      }

      this.saveJsonFile(
        {
          crawlId: job.crawlId,
          website: job.baseUrl,
          exportedAt: new Date().toISOString(),
          total: documents.length,
          documents
        },
        this.documentsFilename(job)
      );
      this.showToast(`Downloaded ${documents.length} extracted document${documents.length === 1 ? '' : 's'}.`, 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error || 'Download failed.');
      this.showToast(message, 'error');
    } finally {
      this.downloadingCrawlId = null;
    }
  }

  async trainCompletedCrawls(): Promise<void> {
    if (!this.canTrainCompletedCrawls()) {
      this.showToast('No completed crawls are ready for training yet.', 'error');
      return;
    }
    if (!window.confirm('This will replace previous training chunks for each completed website crawl and import the latest extracted pages. Continue?')) {
      return;
    }

    await this.trainCrawlJobs(this.availableTrainingRows());
  }

  async createQaList(job: WebsiteCrawlJob): Promise<void> {
    if (job.status !== 'COMPLETED' || this.qaGeneratingCrawlId) return;
    this.qaGeneratingCrawlId = job.crawlId;
    try {
      const result = await firstValueFrom(this.crawlService.createQaList(job.crawlId));
      this.qaLists.set(job.crawlId, result);
      this.showToast(`Created ${result.qaCount} grounded question-answer pairs.`, 'success');
    } catch (error) {
      this.showToast(error instanceof Error ? error.message : String(error || 'QA generation failed.'), 'error');
    } finally {
      this.qaGeneratingCrawlId = null;
    }
  }

  async downloadQaList(job: WebsiteCrawlJob): Promise<void> {
    const artifact = this.qaLists.get(job.crawlId);
    if (!artifact || this.qaDownloadingCrawlId) return;
    this.qaDownloadingCrawlId = job.crawlId;
    try {
      const blob = await firstValueFrom(this.crawlService.downloadQaList(job.crawlId));
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = artifact.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      this.showToast(error instanceof Error ? error.message : String(error || 'QA download failed.'), 'error');
    } finally {
      this.qaDownloadingCrawlId = null;
    }
  }

  private saveJsonFile(payload: unknown, filename: string): void {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  private documentsFilename(job: WebsiteCrawlJob): string {
    const host = this.safeFilenamePart(job.baseUrl.replace(/^https?:\/\//i, '').replace(/\/.*$/, '')) || 'website';
    return `${host}-${job.crawlId}-documents.json`;
  }

  private safeFilenamePart(value: string): string {
    return (value || '')
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^[.-]+|[.-]+$/g, '');
  }

  private counterRows(record: Record<string, number> | null | undefined, preferredOrder: string[]): CounterRow[] {
    if (!record) {
      return [];
    }
    const keys = Object.keys(record).filter(key => Number(record[key]) > 0);
    const order = new Map(preferredOrder.map((key, index) => [key, index]));
    return keys
      .sort((left, right) => (order.get(left) ?? 999) - (order.get(right) ?? 999) || left.localeCompare(right))
      .map(key => ({ key, label: this.counterLabel(key), value: Number(record[key]) }));
  }

  private counterLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  private firstRecord(...records: Array<Record<string, number> | null | undefined>): Record<string, number> | null {
    return records.find(record => record && Object.keys(record).length > 0) ?? null;
  }

  private firstNumber(...values: Array<number | null | undefined>): number | null {
    const value = values.find(item => item !== null && item !== undefined);
    return value ?? null;
  }

  private reportNumber(job: WebsiteCrawlJob, key: keyof NonNullable<WebsiteCrawlStatusResponse['report']>): number | null {
    const value = job.detail?.report?.[key];
    return typeof value === 'number' ? value : null;
  }

  private async trainCrawlJobs(jobsToTrain: WebsiteCrawlJob[]): Promise<void> {
    this.training = true;
    let completed = 0;
    let totalPages = 0;
    let totalChunks = 0;

    try {
      for (const job of jobsToTrain) {
        this.trainingProgress = `Training ${completed + 1}/${jobsToTrain.length}: ${job.baseUrl}`;
        const result = await firstValueFrom(this.crawlService.importCompletedCrawl(
          job.crawlId,
          'Website Knowledge Crawler',
          true,
          'auto'
        ));
        completed += 1;
        totalPages += result.pagesInserted || 0;
        totalChunks += result.chunksCreated || 0;
      }

      this.trainingProgress = '';
      this.showToast(
        `Training completed: ${completed} crawl${completed === 1 ? '' : 's'}, ${totalPages} pages, ${totalChunks} chunks.`,
        'success'
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error || 'Training failed.');
      this.showToast(message, 'error');
    } finally {
      this.training = false;
      this.trainingProgress = '';
    }
  }

  /**
   * One poller per crawl. Re-selecting a job never starts a second stream, and
   * the poller is dropped once the crawl reaches a terminal status.
   */
  private startPolling(crawlId: string): void {
    if (this.pollers.has(crawlId)) {
      return;
    }

    const subscription = this.crawlService.pollCrawlStatus(crawlId).subscribe({
      next: status => {
        this.applyStatus(crawlId, status);
        if (isTerminalStatus(status.status)) {
          this.stopPolling(crawlId);
        }
      },
      error: (message: string) => {
        // A single failed poll must not kill the page. Surface it on the job
        // row and stop this poller; the crawl itself is unaffected.
        this.updateJob(crawlId, job => ({ ...job, error: message }));
        this.stopPolling(crawlId);
        this.showToast(message, 'error');
      }
    });

    this.pollers.set(crawlId, subscription);
  }

  private stopPolling(crawlId: string): void {
    this.pollers.get(crawlId)?.unsubscribe();
    this.pollers.delete(crawlId);
  }

  private applyStatus(crawlId: string, status: WebsiteCrawlStatusResponse): void {
    this.updateJob(crawlId, job => ({
      ...job,
      status: status.status,
      detail: status,
      error: status.error ?? null
    }));
    if (isTerminalStatus(status.status)) {
      const job = this.jobs.find(item => item.crawlId === crawlId);
      if (job) {
        this.loadArtifacts(job);
      }
    }
  }

  private loadHistory(): void {
    this.loadingHistory = true;
    this.crawlService.getCrawlHistory().subscribe({
      next: response => {
        this.loadingHistory = false;
        this.jobs = this.dedupeJobsByBaseUrl(response.items.map(item => this.historyJob(item)));
        if (!this.selectedCrawlId && this.jobs.length) {
          this.selectedCrawlId = this.jobs[0].crawlId;
          this.loadArtifacts(this.jobs[0]);
        }
        this.jobs.filter(job => this.isActive(job.status)).forEach(job => this.startPolling(job.crawlId));
      },
      error: (message: string) => {
        this.loadingHistory = false;
        this.showToast(message, 'error');
      }
    });
  }

  private historyJob(item: WebsiteCrawlHistoryItem): WebsiteCrawlJob {
    return {
      crawlId: item.crawlId,
      baseUrl: item.baseUrl,
      status: item.status,
      createdAt: item.createdAt,
      error: item.error,
      detail: {
        ...item,
        report: null,
        documents: []
      }
    };
  }

  private loadArtifacts(job: WebsiteCrawlJob): void {
    const detail = job.detail;
    if (!detail || this.artifactLoads.has(job.crawlId)) {
      return;
    }
    if (!detail.documentsAvailable && !detail.reportAvailable) {
      return;
    }
    this.artifactLoads.add(job.crawlId);
    let pending = Number(detail.documentsAvailable) + Number(detail.reportAvailable);
    const done = () => {
      pending -= 1;
      if (pending <= 0) {
        this.artifactLoads.delete(job.crawlId);
      }
    };
    if (detail.documentsAvailable) {
      this.crawlService.getCrawlDocuments(job.crawlId).subscribe({
        next: response => {
          this.updateJob(job.crawlId, current => ({
            ...current,
            detail: current.detail ? { ...current.detail, documents: response.items } : null
          }));
          done();
        },
        error: done
      });
    }
    if (detail.reportAvailable) {
      this.crawlService.getCrawlReport(job.crawlId).subscribe({
        next: report => {
          this.updateJob(job.crawlId, current => ({
            ...current,
            detail: current.detail ? { ...current.detail, report } : null
          }));
          done();
        },
        error: done
      });
    }
  }

  private updateJob(crawlId: string, change: (job: WebsiteCrawlJob) => WebsiteCrawlJob): void {
    this.jobs = this.jobs.map(job => (job.crawlId === crawlId ? change(job) : job));
  }

  private removeLocalJobsForBaseUrl(baseUrl: string, keepCrawlId?: string): void {
    const normalized = this.normalizeBaseUrl(baseUrl);
    if (!normalized) {
      return;
    }
    const removeIds = this.jobs
      .filter(job => job.crawlId !== keepCrawlId && this.normalizeBaseUrl(job.baseUrl) === normalized)
      .map(job => job.crawlId);
    removeIds.forEach(crawlId => this.stopPolling(crawlId));
    this.jobs = this.jobs.filter(job => job.crawlId === keepCrawlId || this.normalizeBaseUrl(job.baseUrl) !== normalized);
    if (this.selectedCrawlId && removeIds.includes(this.selectedCrawlId)) {
      this.selectedCrawlId = null;
      this.selectedPage = null;
    }
  }

  private dedupeJobsByBaseUrl(jobs: WebsiteCrawlJob[]): WebsiteCrawlJob[] {
    const seen = new Set<string>();
    const deduped: WebsiteCrawlJob[] = [];
    for (const job of jobs) {
      const key = this.normalizeBaseUrl(job.baseUrl) || job.baseUrl;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      deduped.push(job);
    }
    return deduped;
  }

  private buildRequest(baseUrl: string): WebsiteCrawlRequest {
    return {
      baseUrl,
      maxPages: this.maxPages,
      maxDepth: this.maxDepth,
      concurrency: this.concurrency,
      navigationTimeoutMs: this.navigationTimeoutMs,
      waitAfterLoadMs: this.waitAfterLoadMs,
      scopeMode: this.scopeMode,
      respectRobotsTxt: this.respectRobotsTxt,
      saveRenderedHtml: this.saveRenderedHtml,
      saveScreenshot: this.saveScreenshot,
      interactWithCarousels: this.interactWithCarousels,
      maxSlidesPerCarousel: this.maxSlidesPerCarousel,
      slideTransitionWaitMs: this.slideTransitionWaitMs
    };
  }

  /** Returns the URL to crawl, or null when it is not a usable http(s) address. */
  private normalizeBaseUrl(value: string): string | null {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      return null;
    }
    const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
      const parsed = new URL(candidate);
      if (!['http:', 'https:'].includes(parsed.protocol) || !parsed.hostname) {
        return null;
      }
      // `new URL` accepts junk like "https://not a url" (host "not"), so require
      // a host that actually looks like a hostname before calling the backend.
      if (!/^[a-z0-9.-]+$/i.test(parsed.hostname) || !parsed.hostname.includes('.')) {
        return null;
      }
      // Spring rejects embedded credentials; fail here rather than round-trip.
      if (parsed.username || parsed.password) {
        return null;
      }
      return parsed.toString();
    } catch {
      return null;
    }
  }

  private showToast(message: string, type: 'success' | 'error' = 'success'): void {
    const toast = { id: ++this.toastId, message, type };
    this.toasts.push(toast);
    setTimeout(() => {
      this.toasts = this.toasts.filter(item => item.id !== toast.id);
    }, 4000);
  }

}
